/**
 * AI Assistant — conversation history drawer.
 *
 * Glass drawer over the chat body: search, pinned section first, then one
 * section per scope ("All jobs" / job title — from each conversation's
 * `scope_type` / `job_id`, G1 addendum), slim rows (title · relative time ·
 * message count) with hover-revealed Rename / Pin / Delete, and an inline
 * "Delete? Yes / No" confirmation (no window.confirm).
 *
 * Threads are loaded for the company scope and, when the dock is in a job
 * scope, for that job as well (`GET /api/copilot/conversations?scope=company`
 * and `?job_id=`), merged by id. Selecting a thread from another scope hands
 * the whole conversation to `onSelect`, so the dock can switch its scope chip.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Pin, PinOff, Pencil, Trash2, Check, X, Loader2, MessageSquare, ArrowLeft, Search, Building2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import { cn } from '@/lib/utils';
import { deleteConversation, fetchConversations, patchConversation } from './copilotApi';
import { COMPANY_LABEL, COMPANY_SCOPE, jobScope, normaliseScope, scopeKey, scopeLabel } from './scope';

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '';
  }
}

/** Scope a conversation row belongs to (from `scope_type` / `job_id`; `fallback` for legacy rows). */
export function conversationScope(conv, fallback = COMPANY_SCOPE) {
  if (!conv) return normaliseScope(fallback);
  const type = String(conv.scope_type || conv.scope?.type || '').toLowerCase();
  const jobId = conv.job_id ?? conv.scope?.job_id ?? null;
  if (type === 'company') return COMPANY_SCOPE;
  if (type === 'job' || (type === '' && jobId !== null && jobId !== undefined)) return jobScope(jobId);
  return normaliseScope(fallback);
}

/**
 * Group conversations by scope. Returns `[{ key, scope, label, items }]`, the
 * current scope first, then "All jobs", then other jobs by most recent.
 */
export function groupConversationsByScope(conversations, currentScope, jobs = []) {
  const groups = new Map();
  (conversations || []).forEach((conv) => {
    const scope = conversationScope(conv, currentScope);
    const key = scopeKey(scope);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        scope,
        label: scope.type === 'company' ? COMPANY_LABEL : conv.job_title || scopeLabel(scope, jobs, `Job ${scope.job_id}`),
        items: [],
        latest: 0,
      });
    }
    const group = groups.get(key);
    group.items.push(conv);
    const t = new Date(conv.updated_at || 0).getTime();
    if (Number.isFinite(t) && t > group.latest) group.latest = t;
  });
  const currentKey = scopeKey(currentScope);
  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === currentKey) return -1;
    if (b.key === currentKey) return 1;
    if (a.key === 'company') return -1;
    if (b.key === 'company') return 1;
    return b.latest - a.latest;
  });
}

function IconBtn({ label, onClick, children, className, type = 'button' }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type={type} className={cn('cp-icon-btn cp-icon-btn-sm', className)} onClick={onClick} aria-label={label}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-[11px]">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export default function CopilotHistory({ scope, jobs = [], activeId, onSelect, onBack, onDeleted }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [draft, setDraft] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');

  const current = useMemo(() => normaliseScope(scope), [scope]);
  const currentKey = scopeKey(current);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    const requests = [fetchConversations(COMPANY_SCOPE)];
    if (current.type === 'job') requests.push(fetchConversations(current));
    Promise.allSettled(requests)
      .then((results) => {
        if (cancelled) return;
        if (results.every((r) => r.status === 'rejected')) {
          setError('Could not load history.');
          return;
        }
        const seen = new Set();
        const merged = [];
        results.forEach((r, idx) => {
          if (r.status !== 'fulfilled') return;
          // Rows from the job request default to that job when they carry no scope fields.
          const fallback = idx === 0 ? COMPANY_SCOPE : current;
          (r.value || []).forEach((conv) => {
            if (!conv || seen.has(conv.id)) return;
            seen.add(conv.id);
            merged.push(conv.scope_type ? conv : { ...conv, scope_type: conversationScope(conv, fallback).type, job_id: conv.job_id ?? (fallback.type === 'job' ? fallback.job_id : null) });
          });
        });
        merged.sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime());
        setItems(merged);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load history.');
      });
    return () => {
      cancelled = true;
    };
  }, [current, currentKey]);

  const update = (id, patch) => setItems((list) => (list || []).map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const togglePin = async (conv) => {
    setBusyId(conv.id);
    try {
      await patchConversation(conv.id, { pinned: !conv.pinned });
      update(conv.id, { pinned: !conv.pinned });
    } catch {
      toast.error('Could not update thread');
    } finally {
      setBusyId(null);
    }
  };

  const saveTitle = async (conv) => {
    const title = draft.trim();
    setEditingId(null);
    if (!title || title === conv.title) return;
    setBusyId(conv.id);
    try {
      await patchConversation(conv.id, { title });
      update(conv.id, { title });
    } catch {
      toast.error('Could not rename thread');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (conv) => {
    setConfirmId(null);
    setBusyId(conv.id);
    try {
      await deleteConversation(conv.id);
      setItems((list) => (list || []).filter((c) => c.id !== conv.id));
      if (onDeleted) onDeleted(conv.id);
      toast.success('Thread archived');
    } catch {
      toast.error('Could not delete thread');
    } finally {
      setBusyId(null);
    }
  };

  const q = query.trim().toLowerCase();
  const visible = (items || []).filter((c) => !q || String(c.title || '').toLowerCase().includes(q));
  const pinned = visible.filter((c) => Boolean(c.pinned));
  const groups = useMemo(() => groupConversationsByScope(visible.filter((c) => !c.pinned), current, jobs), [visible, current, jobs]);

  const renderRow = (conv, { showScope = false } = {}) => {
    const isActive = conv.id === activeId;
    const isEditing = editingId === conv.id;
    const isConfirming = confirmId === conv.id;
    const busy = busyId === conv.id;
    const convScope = conversationScope(conv, current);
    const scopeName = convScope.type === 'company' ? COMPANY_LABEL : conv.job_title || scopeLabel(convScope, jobs, `Job ${convScope.job_id}`);
    const meta = [
      when(conv.updated_at),
      conv.message_count ? `${conv.message_count} messages` : null,
      showScope ? scopeName : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <li
        key={conv.id}
        className={cn(
          'group flex min-h-[40px] items-center gap-2 rounded-xl px-2 py-1 transition-colors',
          isActive ? 'cp-mint-fill' : 'hover:bg-[hsl(var(--cp-mint)/0.08)]'
        )}
        data-active={isActive ? 'true' : undefined}
        data-scope={scopeKey(convScope)}
      >
        {conv.pinned ? (
          <Pin className="cp-text-brand h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <MessageSquare className="cp-faint h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}

        {isEditing ? (
          <form
            className="flex flex-1 items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              saveTitle(conv);
            }}
          >
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditingId(null);
              }}
              className="cp-focus h-7 min-w-0 flex-1 rounded-lg bg-transparent px-2 text-[13px] outline-none"
              style={{ border: '1px solid var(--cp-glass-border)', color: 'var(--cp-text)' }}
              aria-label="Thread title"
            />
            <IconBtn type="submit" label="Save title">
              <Check className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn label="Cancel rename" onClick={() => setEditingId(null)}>
              <X className="h-3.5 w-3.5" />
            </IconBtn>
          </form>
        ) : (
          <>
            <button type="button" className="cp-focus min-w-0 flex-1 rounded-lg text-left" onClick={() => onSelect(conv)}>
              <div className="truncate text-[13px] font-medium leading-4">{conv.title || 'Untitled thread'}</div>
              {meta && <div className="cp-muted truncate text-[11px] leading-4">{meta}</div>}
            </button>

            {isConfirming ? (
              <span className="flex shrink-0 items-center gap-1 text-[11px]" role="group" aria-label="Confirm delete">
                <span className="cp-text-bad font-medium">Delete?</span>
                <button type="button" className="cp-pill-bad cp-focus rounded-full px-2 py-0.5 font-semibold" onClick={() => remove(conv)}>
                  Yes
                </button>
                <button
                  type="button"
                  className="cp-focus rounded-full px-2 py-0.5 font-medium hover:bg-black/5 dark:hover:bg-white/10"
                  onClick={() => setConfirmId(null)}
                >
                  No
                </button>
              </span>
            ) : (
              <div
                className={cn(
                  'flex shrink-0 items-center gap-0.5',
                  !busy && 'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100'
                )}
              >
                {busy ? (
                  <Loader2 className="cp-faint h-3.5 w-3.5 motion-safe:animate-spin" aria-label="Working" />
                ) : (
                  <>
                    <IconBtn
                      label="Rename"
                      onClick={() => {
                        setDraft(conv.title || '');
                        setEditingId(conv.id);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </IconBtn>
                    <IconBtn label={conv.pinned ? 'Unpin' : 'Pin'} onClick={() => togglePin(conv)}>
                      {conv.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                    </IconBtn>
                    <IconBtn label="Delete" className="cp-text-bad" onClick={() => setConfirmId(conv.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </IconBtn>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </li>
    );
  };

  const emptyText = q ? 'No threads match your search' : current.type === 'job' ? 'No conversations yet for this role' : 'No conversations yet';

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center gap-1.5 px-2 py-1.5" style={{ borderBottom: '1px solid var(--cp-glass-border)' }}>
          <IconBtn label="Back to chat" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </IconBtn>
          <span className="text-[13px] font-semibold">History</span>
          <label className="cp-composer ml-auto flex h-8 w-44 items-center gap-1.5 rounded-full px-2.5" style={{ borderRadius: 9999 }}>
            <Search className="cp-faint h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search threads"
              className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-[var(--cp-text-faint)]"
              style={{ color: 'var(--cp-text)' }}
              aria-label="Search threads"
            />
          </label>
        </div>

        <div className="cp-scroll flex-1 overflow-y-auto p-2">
          {items === null && !error && (
            <div className="space-y-2" aria-busy="true" aria-label="Loading history">
              {[0, 1, 2].map((i) => (
                <div key={i} className="cp-shimmer h-10 w-full" />
              ))}
            </div>
          )}
          {error && <div className="cp-banner cp-banner-bad">{error}</div>}
          {items && !visible.length && !error && <div className="cp-muted py-8 text-center text-xs">{emptyText}</div>}

          {pinned.length > 0 && (
            <section className="mb-2" aria-label="Pinned threads">
              <div className="cp-label flex items-center gap-1 px-2 pb-1">
                <Pin className="h-3 w-3" aria-hidden="true" /> Pinned
              </div>
              <ul className="space-y-0.5">{pinned.map((conv) => renderRow(conv, { showScope: true }))}</ul>
            </section>
          )}
          {groups.map((group) => (
            <section key={group.key} className="mb-2" aria-label={`${group.label} threads`} data-testid="copilot-history-group" data-scope={group.key}>
              <div className="cp-label flex items-center gap-1 px-2 pb-1">
                {group.scope.type === 'company' ? (
                  <Building2 className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <Briefcase className="h-3 w-3" aria-hidden="true" />
                )}
                <span className="truncate normal-case tracking-normal">{group.label}</span>
                {group.key === currentKey && <span className="cp-chip cp-chip-static cp-chip-sm cp-chip-mint ml-1 normal-case tracking-normal">current</span>}
              </div>
              <ul className="space-y-0.5">{group.items.map((conv) => renderRow(conv))}</ul>
            </section>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
